import { Column } from "@/components/data-table";
import { Form, useSchemaForm } from "@/components/form";
import { Dialog, DialogProps, ModalHeader } from "@/components/modal";
import { useCallback } from "react";
import { toExportFormat } from "../shared/utils";
import ExportService from "../api/export-service";
import { useAuthContext } from "@/features/auth/contexts/auth-context";
import { z } from "zod/v4";
import { useFiltersReturn } from "@/components/filters";

interface CsvExportDialogDataProps<T = any> {
    dialogTitle?: string,
    defaultFileTitle: string,
    fetchFn: (query?: Record<string, any>) => Promise<{ result: T[] }>
    filterData: useFiltersReturn,
    columns: Column<T>[],
}

export interface CsvExportDialogProps<T = any> extends DialogProps, CsvExportDialogDataProps<T> {
}

export default function CsvExportDialog<T = any>({
    fetchFn,
    columns,
    filterData,
    defaultFileTitle,
    dialogTitle = "Exportar .csv",
    ...props
}: CsvExportDialogProps<T>) {
    return <Dialog size="sm" {...props}>
        <CsvExportDialogContent {...{
            fetchFn,
            columns,
            filterData,
            defaultFileTitle,
            dialogTitle
        }} />
    </Dialog>
}

const csvFormSchema = z.object({
    title: z.string().nonempty("Este campo es requerido")
})

function CsvExportDialogContent({
    fetchFn,
    columns,
    filterData,
    defaultFileTitle,
    dialogTitle = "Exportar .csv",
}: CsvExportDialogDataProps) {
    const { authData } = useAuthContext();

    const handleExport = useCallback(async (title: string) => {
        const queryWithoutPagination = filterData.filters.reduce((prev, filter) => {
            if (filter.type === "pagination") {
                delete prev[filter.pageName];
                delete prev[filter.pageSizeName];
            }
            return prev;
        }, { ...filterData.filterData });

        const { result } = await fetchFn(queryWithoutPagination);
        const [exportColumns, data] = toExportFormat(columns, result);
        ExportService.exportCSV({
            columns: exportColumns,
            data,
            documentName: title,
            creator: authData.nombre,
        })
    }, [fetchFn, columns, filterData])

    const csvForm = useSchemaForm({
        schema: csvFormSchema,
        onSubmit: async ({ value }) => {
            const { title } = value;
            await handleExport(title);
        },
        defaultValues: {
            title: defaultFileTitle
        },
        submitOnce: true,
        closeModalOnSubmit: true,
    })

    return <>
        <ModalHeader>{dialogTitle}</ModalHeader>
        <Form form={csvForm} padding={3} gap={3}>
            <Form.TextField name="title" label="Título" />
            <Form.SubmitButton>Exportar</Form.SubmitButton>
        </Form>
    </>
}